// features/super-admin/pages/TenantDetailPage.tsx
/**
 * SUPER ADMIN — TENANT DETAIL
 * Comprehensive tenant overview with subscription management and business listing.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Building2, Store, Users, Calendar,
    Mail, Phone, Shield, CheckCircle2, XCircle,
    Pencil, X, CreditCard, Layers, TrendingUp,
    Clock, Globe, MapPin, Award, Star, Zap,
    Activity, Eye, ExternalLink, Copy, Sparkles, MessageSquare,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface TenantDetail {
    id: string; name: string; slug: string; businessType: string;
    logoUrl: string | null; email: string | null; phone: string | null;
    address: string | null; taxId: string | null;
    isActive: boolean; subscriptionTier: string;
    businessModel?: 'single' | 'multi';
    isSubscribed?: boolean;
    subscriptionPin?: string | null;
    subscriptionExpiresAt?: string | null;
    businessesCount: number; staffCount: number;
    businesses: Array<{
        id: string; name: string; shortCode: string; type: string;
        isActive: boolean; createdAt: string;
    }>;
    subscription: {
        id: string; planId: string; status: string;
        trialStartAt: string | null; trialEndAt: string | null;
        currentPeriodStart: string | null; currentPeriodEnd: string | null;
    } | null;
    createdAt: string; updatedAt: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const TIER_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
    enterprise: { icon: Award, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Enterprise' },
    business: { icon: Building2, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200', label: 'Business' },
    starter: { icon: Zap, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', label: 'Starter' },
    free: { icon: Star, color: 'text-slate-600', bg: 'bg-slate-50 border-slate-200', label: 'Free' },
};

const SUBSCRIPTION_STATUS: Record<string, { variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    trial: { variant: 'info', label: 'Trial' },
    past_due: { variant: 'danger', label: 'Past Due' },
    cancelled: { variant: 'neutral', label: 'Cancelled' },
    expired: { variant: 'danger', label: 'Expired' },
};

// ============================================================
// FORMATTERS
// ============================================================

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

const formatDateTime = (date: string) => {
    try { return new Date(date).toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return 'N/A'; }
};

const getInitials = (name: string) => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

// ============================================================
// MAIN COMPONENT
// ============================================================

export function TenantDetailPage() {
    const { tenantId } = useParams<{ tenantId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [showEditSubscription, setShowEditSubscription] = useState(false);
    const [subForm, setSubForm] = useState({ subscriptionTier: '', status: '' });

    const [showOfflineModal, setShowOfflineModal] = useState(false);
    const [offlineForm, setOfflineForm] = useState({
        agreementRef: '',
        durationMonths: 12,
        businessModel: 'multi' as 'single' | 'multi',
        agreementNotes: '',
    });

    const activateOfflineAgreementMutation = useMutation({
        mutationFn: (payload: any) =>
            apiClient.post(`/super-admin/tenants/${tenantId}/subscription/offline-agreement`, payload),
        onSuccess: (res: any) => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenant', tenantId] });
            addToast({
                type: 'success',
                title: 'Offline Agreement Activated',
                message: res?.data?.data?.message || 'Tenant granted full access with zero gating!',
            });
            setShowOfflineModal(false);
        },
        onError: (err: any) =>
            addToast({ type: 'error', title: 'Activation Failed', message: err?.message }),
    });

    const { data: tenant, isLoading, isError } = useQuery<TenantDetail>({
        queryKey: ['super-admin-tenant', tenantId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/super-admin/tenants/${tenantId}`);
            return data?.data;
        },
        enabled: !!tenantId,
    });

    const toggleTenantMutation = useMutation({
        mutationFn: (activate: boolean) =>
            activate
                ? apiClient.post(`/super-admin/tenants/${tenantId}/activate`)
                : apiClient.post(`/super-admin/tenants/${tenantId}/deactivate`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenant', tenantId] });
            addToast({ type: 'success', title: 'Tenant Updated' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const updateSubscriptionMutation = useMutation({
        mutationFn: (payload: any) => apiClient.post(`/super-admin/tenants/${tenantId}/subscription`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenant', tenantId] });
            addToast({ type: 'success', title: 'Subscription Updated' });
            setShowEditSubscription(false);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const activateSubscriptionMutation = useMutation({
        mutationFn: () => apiClient.post(`/super-admin/tenants/${tenantId}/subscription/activate`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenant', tenantId] });
            addToast({ type: 'success', title: 'Subscription Activated', message: 'Tenant subscription set to active.' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Activation Failed', message: err?.message }),
    });

    const generatePinMutation = useMutation({
        mutationFn: () => apiClient.post(`/super-admin/tenants/${tenantId}/subscription/generate-pin`),
        onSuccess: (res: any) => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenant', tenantId] });
            const pinCode = res?.data?.data?.pin || res?.data?.pin;
            addToast({
                type: 'success',
                title: 'PIN Dispatched',
                message: `6-Digit PIN ${pinCode ? `(${pinCode}) ` : ''}generated and sent via Resend email!`,
            });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'PIN Generation Failed', message: err?.message }),
    });

    const cancelSubscriptionMutation = useMutation({
        mutationFn: () => apiClient.post(`/super-admin/tenants/${tenantId}/subscription/cancel`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenant', tenantId] });
            addToast({ type: 'warning', title: 'Subscription Cancelled', message: 'Tenant subscription has been deactivated.' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Cancellation Failed', message: err?.message }),
    });

    const impersonateMutation = useMutation({
        mutationFn: () => apiClient.post(`/super-admin/tenants/${tenantId}/impersonate`),
        onSuccess: (res: any) => {
            const data = res?.data?.data;
            if (data?.tokens?.accessToken) {
                const currentAccess = localStorage.getItem('accessToken');
                const currentRefresh = localStorage.getItem('refreshToken');
                if (currentAccess) localStorage.setItem('originalPlatformAccessToken', currentAccess);
                if (currentRefresh) localStorage.setItem('originalPlatformRefreshToken', currentRefresh);
                localStorage.setItem('impersonatedTenantName', data.tenant.name);
                localStorage.setItem('accessToken', data.tokens.accessToken);
                localStorage.setItem('refreshToken', data.tokens.refreshToken);
                localStorage.setItem('tenantId', data.tenant.id);
                localStorage.setItem('tenantSlug', data.tenant.slug);
                if (data.selectedBusinessId) localStorage.setItem('selectedBusinessId', data.selectedBusinessId);
                if (data.selectedBranchId) localStorage.setItem('selectedBranchId', data.selectedBranchId);
                addToast({
                    type: 'success',
                    title: 'Entered Support Mode',
                    message: `Now viewing store as ${data.tenant.name}`,
                });
                window.location.href = '/dashboard';
            }
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Impersonation Failed',
                message: err?.response?.data?.message || 'Could not access tenant store',
            });
        },
    });

    const extendSubscriptionMutation = useMutation({
        mutationFn: (days: number) => {
            const baseDate = tenant?.subscriptionExpiresAt
                ? new Date(tenant.subscriptionExpiresAt)
                : (tenant?.subscription?.currentPeriodEnd ? new Date(tenant.subscription.currentPeriodEnd) : new Date());
            const effectiveBase = baseDate.getTime() > Date.now() ? baseDate : new Date();
            const newExpiry = new Date(effectiveBase.getTime() + days * 24 * 60 * 60 * 1000);
            return apiClient.post(`/super-admin/tenants/${tenantId}/subscription`, {
                expiresAt: newExpiry.toISOString(),
                status: 'active',
                isSubscribed: true,
            });
        },
        onSuccess: (_res, days) => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-tenant', tenantId] });
            addToast({
                type: 'success',
                title: 'Subscription Extended',
                message: `Added +${days} days to subscription period!`,
            });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Extension Failed', message: err?.message }),
    });

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-2xl border-4 border-violet-200 border-t-violet-600" />
        </div>
    );

    if (isError || !tenant) return (
        <div className="min-h-screen flex items-center justify-center">
            <ErrorState title="Tenant not found" onBack={() => navigate('/super-admin/tenants')} />
        </div>
    );

    const tierConfig = TIER_CONFIG[tenant.subscriptionTier] || TIER_CONFIG.free;
    const TierIcon = tierConfig.icon;
    const subStatus = tenant.subscription?.status || 'active';
    const statusConfig = SUBSCRIPTION_STATUS[subStatus] || SUBSCRIPTION_STATUS.active;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Back */}
                <button onClick={() => navigate('/super-admin/tenants')}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-violet-600 group">
                    <ArrowLeft size={16} className="group-hover:-translate-x-1" /> Back to Tenants
                </button>

                {/* ========== HERO CARD ========== */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-white rounded-3xl shadow-xl border border-slate-200">
                    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-violet-500 via-purple-600 to-indigo-600" />
                    <div className="p-8 lg:p-10">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-5">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl shadow-xl">
                                    {getInitials(tenant.name)}
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-2xl lg:text-3xl font-black text-slate-900">{tenant.name}</h1>
                                        <Badge variant={tenant.isActive ? 'success' : 'danger'} size="md">
                                            {tenant.isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                        <span className={clsx('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border', tierConfig.bg, tierConfig.color)}>
                                            <TierIcon size={12} />{tierConfig.label}
                                        </span>
                                    </div>
                                    <code className="text-sm text-slate-400 font-mono">@{tenant.slug}</code>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                                        {tenant.email && <span className="flex items-center gap-1"><Mail size={10} />{tenant.email}</span>}
                                        {tenant.phone && <span className="flex items-center gap-1"><Phone size={10} />{tenant.phone}</span>}
                                        <span className="flex items-center gap-1"><Calendar size={10} />Joined {formatDate(tenant.createdAt)}</span>
                                        {tenant.address && <span className="flex items-center gap-1"><MapPin size={10} />{tenant.address}</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <button
                                    onClick={() => impersonateMutation.mutate()}
                                    disabled={impersonateMutation.isPending}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg hover:opacity-95 transition-all">
                                    <Shield size={16} />
                                    {impersonateMutation.isPending ? 'Accessing...' : 'Support Mode (Impersonate)'}
                                </button>
                                {tenant.phone && (
                                    <a
                                        href={`https://wa.me/${tenant.phone.replace(/[^0-9]/g, '')}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 transition-all">
                                        <MessageSquare size={16} /> WhatsApp
                                    </a>
                                )}
                                <button
                                    onClick={() => toggleTenantMutation.mutate(!tenant.isActive)}
                                    disabled={toggleTenantMutation.isPending}
                                    className={clsx(
                                        'flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm shadow-sm transition-all',
                                        tenant.isActive
                                            ? 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100'
                                            : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                                    )}>
                                    {tenant.isActive ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
                                    {tenant.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== STATS ROW ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { label: 'Businesses', value: tenant.businessesCount, icon: Store, gradient: 'from-emerald-500 to-teal-600' },
                        { label: 'Staff', value: tenant.staffCount, icon: Users, gradient: 'from-blue-500 to-indigo-600' },
                        { label: 'Type', value: tenant.businessType, icon: Layers, gradient: 'from-purple-500 to-violet-600', isString: true },
                        { label: 'Status', value: tenant.isActive ? 'Active' : 'Inactive', icon: tenant.isActive ? CheckCircle2 : XCircle, gradient: tenant.isActive ? 'from-emerald-500 to-teal-600' : 'from-rose-500 to-pink-600', isString: true },
                    ].map((stat, i) => (
                        <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all">
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
                                <stat.icon className="w-4 h-4 text-white" />
                            </div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                            <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                        </motion.div>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* LEFT: Subscription + Businesses */}
                    <div className="lg:col-span-2 space-y-5">
                        {/* Subscription Card */}
                        <Card>
                            <CardHeader gradient>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                            <CreditCard size={18} className="text-violet-500" /> Subscription & Access
                                        </h3>
                                        <Badge variant={tenant.isSubscribed ? 'success' : 'danger'} size="sm">
                                            {tenant.isSubscribed ? 'Subscribed' : 'Not Subscribed'}
                                        </Badge>
                                        {tenant.businessModel && (
                                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                                                {tenant.businessModel === 'single' ? 'Single Business' : 'Multi-Business'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => {
                                            setSubForm({ subscriptionTier: tenant.subscriptionTier, status: tenant.subscription?.status || 'active' });
                                            setShowEditSubscription(true);
                                        }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                                            <Pencil size={12} /> Custom Edit
                                        </button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Platform Admin Instant Action Buttons */}
                                <div className="p-3.5 bg-gradient-to-r from-violet-50 via-indigo-50 to-emerald-50 rounded-2xl border border-violet-100 flex flex-wrap items-center justify-between gap-2.5">
                                    <div>
                                        <p className="text-xs font-bold text-slate-800">Platform Admin Controls</p>
                                        <p className="text-[11px] text-slate-500">Instantly provision subscription, generate 6-digit email PINs, or cancel upon expiry.</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {!tenant.isSubscribed ? (
                                            <button
                                                onClick={() => activateSubscriptionMutation.mutate()}
                                                disabled={activateSubscriptionMutation.isPending}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm transition-all"
                                            >
                                                <CheckCircle2 size={13} />
                                                {activateSubscriptionMutation.isPending ? 'Activating...' : 'Activate Subscription'}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => cancelSubscriptionMutation.mutate()}
                                                disabled={cancelSubscriptionMutation.isPending}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 hover:bg-rose-100 rounded-lg transition-all"
                                            >
                                                <XCircle size={13} />
                                                {cancelSubscriptionMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
                                            </button>
                                        )}

                                        <button
                                            onClick={() => setShowOfflineModal(true)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-300 hover:bg-emerald-100 rounded-lg shadow-sm transition-all"
                                        >
                                            <Sparkles size={13} className="text-emerald-600" />
                                            Activate Offline Agreement (Full Access)
                                        </button>

                                        <button
                                            onClick={() => generatePinMutation.mutate()}
                                            disabled={generatePinMutation.isPending}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-violet-700 bg-violet-100 hover:bg-violet-200 rounded-lg transition-all"
                                        >
                                            <Mail size={13} />
                                            {generatePinMutation.isPending ? 'Sending...' : 'Generate & Send PIN'}
                                        </button>
                                    </div>
                                </div>

                                {/* Quick Extension Renewal */}
                                <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-200 rounded-2xl flex-wrap gap-2.5">
                                    <div className="flex items-center gap-2">
                                        <Clock size={16} className="text-violet-600" />
                                        <div>
                                            <p className="text-xs font-bold text-slate-800">Quick Subscription Renewal</p>
                                            <p className="text-[11px] text-slate-500">Add duration to current expiry with 1 click</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {[
                                            { label: '+14 Days Grace', days: 14 },
                                            { label: '+1 Month', days: 30 },
                                            { label: '+3 Months', days: 90 },
                                            { label: '+1 Year', days: 365 },
                                        ].map(item => (
                                            <button
                                                key={item.days}
                                                type="button"
                                                disabled={extendSubscriptionMutation.isPending}
                                                onClick={() => extendSubscriptionMutation.mutate(item.days)}
                                                className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-white border border-slate-200 hover:border-violet-300 hover:text-violet-700 text-slate-700 shadow-sm transition-all disabled:opacity-50">
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Active PIN Notice if unverified */}
                                {tenant.subscriptionPin && (
                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between text-xs text-amber-900">
                                        <div className="flex items-center gap-2">
                                            <Shield size={16} className="text-amber-600" />
                                            <span>Pending 6-Digit Activation Code:</span>
                                            <span className="font-mono font-extrabold text-sm px-2.5 py-0.5 rounded bg-amber-200/80 tracking-widest text-amber-950">
                                                {tenant.subscriptionPin}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(tenant.subscriptionPin || '');
                                                addToast({ type: 'info', title: 'PIN Copied to Clipboard' });
                                            }}
                                            className="flex items-center gap-1 text-[11px] font-semibold text-amber-800 hover:text-amber-950 px-2 py-1 rounded bg-amber-100"
                                        >
                                            <Copy size={11} /> Copy PIN
                                        </button>
                                    </div>
                                )}

                                {tenant.subscription ? (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className="p-3 bg-slate-50 rounded-xl">
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Plan</p>
                                            <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mt-0.5">
                                                <TierIcon size={14} className={tierConfig.color} />
                                                {tierConfig.label}
                                            </p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl">
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Status</p>
                                            <Badge variant={tenant.isSubscribed ? 'success' : 'danger'} size="sm" className="mt-0.5">
                                                {tenant.isSubscribed ? 'Active' : 'Unsubscribed'}
                                            </Badge>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl">
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Structure</p>
                                            <p className="text-sm font-bold text-slate-900 mt-0.5 capitalize">
                                                {tenant.businessModel || 'Single'}
                                            </p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl">
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Expires / Ends</p>
                                            <p className="text-sm font-bold text-slate-900 mt-0.5">
                                                {tenant.subscriptionExpiresAt ? formatDate(tenant.subscriptionExpiresAt) : (tenant.subscription.currentPeriodEnd ? formatDate(tenant.subscription.currentPeriodEnd) : 'No expiry')}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-6">
                                        <CreditCard size={28} className="text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm text-slate-500">No active plan profile recorded</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Businesses List */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <Store size={18} className="text-emerald-500" /> Businesses ({tenant.businesses.length})
                                </h3>
                            </CardHeader>
                            <CardContent>
                                {tenant.businesses.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Store size={32} className="text-slate-300 mx-auto mb-3" />
                                        <p className="text-sm text-slate-500">No businesses created yet</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {tenant.businesses.map(b => (
                                            <div key={b.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition-all">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold">
                                                            {getInitials(b.name)}
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-sm text-slate-800">{b.name}</p>
                                                            <code className="text-[10px] text-slate-400">{b.shortCode}</code>
                                                        </div>
                                                    </div>
                                                    <Badge variant={b.isActive ? 'success' : 'danger'} size="sm">
                                                        {b.isActive ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                                    <span className="capitalize">{b.type}</span>
                                                    <span>·</span>
                                                    <span>Created {formatDate(b.createdAt)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* RIGHT: Quick Info */}
                    <div className="space-y-5">
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <Shield size={18} className="text-blue-500" /> Tenant Info
                                </h3>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {[
                                        { label: 'Tenant ID', value: tenant.id, mono: true },
                                        { label: 'Slug', value: tenant.slug, mono: true },
                                        { label: 'Business Type', value: tenant.businessType },
                                        { label: 'Email', value: tenant.email || 'N/A' },
                                        { label: 'Phone', value: tenant.phone || 'N/A' },
                                        { label: 'Tax ID', value: tenant.taxId || 'N/A' },
                                        { label: 'Created', value: formatDateTime(tenant.createdAt) },
                                        { label: 'Updated', value: formatDateTime(tenant.updatedAt) },
                                    ].map(info => (
                                        <div key={info.label} className="flex justify-between items-center py-1.5">
                                            <span className="text-xs text-slate-400">{info.label}</span>
                                            <span className={clsx('text-xs font-medium text-slate-700', info.mono && 'font-mono text-[11px]')}>
                                                {info.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* ========== EDIT SUBSCRIPTION MODAL ========== */}
                <AnimatePresence>
                    {showEditSubscription && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowEditSubscription(false)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                                <div className="bg-gradient-to-r from-violet-600 to-purple-700 px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-lg font-bold text-white">Edit Subscription</h2>
                                        <button onClick={() => setShowEditSubscription(false)}
                                            className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30"><X size={16} /></button>
                                    </div>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Plan</label>
                                        <select value={subForm.subscriptionTier} onChange={e => setSubForm({ ...subForm, subscriptionTier: e.target.value })}
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400">
                                            <option value="free">Free</option>
                                            <option value="starter">Starter</option>
                                            <option value="business">Business</option>
                                            <option value="enterprise">Enterprise</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Status</label>
                                        <select value={subForm.status} onChange={e => setSubForm({ ...subForm, status: e.target.value })}
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400">
                                            <option value="trial">Trial</option>
                                            <option value="active">Active</option>
                                            <option value="past_due">Past Due</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t">
                                    <button onClick={() => setShowEditSubscription(false)}
                                        className="px-5 py-2.5 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-white transition">Cancel</button>
                                    <button onClick={() => updateSubscriptionMutation.mutate(subForm)}
                                        disabled={updateSubscriptionMutation.isPending}
                                        className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-violet-700 transition-all disabled:opacity-50 flex items-center gap-2">
                                        {updateSubscriptionMutation.isPending ? <Spinner size="xs" variant="white" /> : 'Save'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* Offline Agreement Onboarding Modal */}
                    {showOfflineModal && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowOfflineModal(false)}>
                            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
                                onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
                                <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Sparkles size={18} className="text-emerald-200" />
                                            <div>
                                                <h2 className="text-base font-bold text-white">Offline Agreement Onboarding</h2>
                                                <p className="text-xs text-emerald-100">Unlock 100% features with zero gating for signed offline contracts</p>
                                            </div>
                                        </div>
                                        <button onClick={() => setShowOfflineModal(false)}
                                            className="p-1.5 rounded-xl bg-white/20 text-white hover:bg-white/30"><X size={16} /></button>
                                    </div>
                                </div>

                                <div className="p-6 space-y-4">
                                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900">
                                        <p className="font-bold">Client: {tenant.name} ({tenant.slug})</p>
                                        <p className="mt-0.5 text-emerald-700">
                                            Activating this tenant will immediately set subscription active, clear all PIN gates, and grant unrestricted platform access across all modules.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                                            Agreement / Contract Reference #
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. OFFLINE-AGR-2026-001"
                                            value={offlineForm.agreementRef}
                                            onChange={e => setOfflineForm({ ...offlineForm, agreementRef: e.target.value })}
                                            className="w-full px-3.5 py-2.5 border-2 border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 font-mono"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                                                Contract Duration
                                            </label>
                                            <select
                                                value={offlineForm.durationMonths}
                                                onChange={e => setOfflineForm({ ...offlineForm, durationMonths: parseInt(e.target.value) })}
                                                className="w-full px-3.5 py-2.5 border-2 border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                            >
                                                <option value={12}>1 Year (12 Months)</option>
                                                <option value={24}>2 Years (24 Months)</option>
                                                <option value={36}>3 Years (36 Months)</option>
                                                <option value={60}>5 Years (60 Months)</option>
                                                <option value={120}>10 Years (Enterprise Long-Term)</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                                                Business Structure
                                            </label>
                                            <select
                                                value={offlineForm.businessModel}
                                                onChange={e => setOfflineForm({ ...offlineForm, businessModel: e.target.value as any })}
                                                className="w-full px-3.5 py-2.5 border-2 border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                            >
                                                <option value="multi">Multi-Business (Enterprise)</option>
                                                <option value="single">Single Business (Standard)</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                                            Contract Scope & Custom Terms Notes
                                        </label>
                                        <textarea
                                            rows={3}
                                            placeholder="Detail key terms agreed off-app (e.g. customized hardware on-site training, 5 branches included, quarterly review SLA)..."
                                            value={offlineForm.agreementNotes}
                                            onChange={e => setOfflineForm({ ...offlineForm, agreementNotes: e.target.value })}
                                            className="w-full px-3.5 py-2 border-2 border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t">
                                    <button onClick={() => setShowOfflineModal(false)}
                                        className="px-4 py-2 border-2 border-slate-300 rounded-xl font-semibold text-xs hover:bg-white transition text-slate-700">Cancel</button>
                                    <button
                                        onClick={() => activateOfflineAgreementMutation.mutate(offlineForm)}
                                        disabled={activateOfflineAgreementMutation.isPending}
                                        className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20 hover:from-emerald-700 transition-all disabled:opacity-50 flex items-center gap-1.5"
                                    >
                                        <CheckCircle2 size={14} />
                                        {activateOfflineAgreementMutation.isPending ? 'Activating Client...' : 'Confirm & Activate Zero-Gating'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}